import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from "react";
import { cn } from "@/lib/cn";

const LENGTH = 6;

export interface CodeInputProps {
  value: string;
  onChange: (code: string) => void;
  /** Called once all six digits are in (typed, pasted or filled by the browser). */
  onComplete?: (code: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  autoFocus?: boolean;
  /** id of the element describing the field (hint or error). */
  describedBy?: string;
  label?: string;
}

/**
 * Six boxes for an emailed code. Paste "123 456", "123-456" or the whole email line and the digits fill in;
 * the browser's one-time-code autofill works too. Arrow keys, Home and End move between boxes, Backspace
 * steps back.
 */
export function CodeInput({ value, onChange, onComplete, disabled, invalid, autoFocus, describedBy, label = "6-digit code" }: CodeInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length: LENGTH }, (_, i) => value[i] ?? "");

  useEffect(() => {
    if (autoFocus) refs.current[Math.min(value.length, LENGTH - 1)]?.focus();
    // Only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const focus = (i: number) => {
    const el = refs.current[Math.max(0, Math.min(LENGTH - 1, i))];
    el?.focus();
    el?.select();
  };

  function commit(next: string, focusAt?: number) {
    const clean = next.replace(/\D/g, "").slice(0, LENGTH);
    onChange(clean);
    if (focusAt !== undefined) focus(focusAt);
    if (clean.length === LENGTH) onComplete?.(clean);
  }

  function fillFrom(i: number, text: string) {
    const incoming = text.replace(/\D/g, "");
    if (!incoming) return;
    // A full code always replaces what is there, wherever it was pasted.
    if (incoming.length >= LENGTH) return commit(incoming.slice(0, LENGTH), LENGTH - 1);
    const next = (value.slice(0, i) + incoming + value.slice(i + incoming.length)).slice(0, LENGTH);
    commit(next, Math.min(i + incoming.length, LENGTH - 1));
  }

  function onKeyDown(i: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (digits[i]) commit(value.slice(0, i) + value.slice(i + 1), i);
      else if (i > 0) commit(value.slice(0, i - 1) + value.slice(i), i - 1);
    } else if (e.key === "Delete") {
      e.preventDefault();
      commit(value.slice(0, i) + value.slice(i + 1), i);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focus(i - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focus(i + 1);
    } else if (e.key === "Home") {
      e.preventDefault();
      focus(0);
    } else if (e.key === "End") {
      e.preventDefault();
      focus(LENGTH - 1);
    }
  }

  function onPaste(i: number, e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    fillFrom(i, e.clipboardData.getData("text"));
  }

  return (
    <div role="group" aria-label={label} aria-describedby={describedBy} className="flex items-center gap-2 sm:gap-2.5">
      {digits.map((d, i) => (
        <span key={i} className="contents">
          {i === LENGTH / 2 && <span className="h-0.5 w-2 shrink-0 rounded-full bg-border-strong" aria-hidden="true" />}
          <input
            ref={(el) => {
              refs.current[i] = el;
            }}
            value={d}
            disabled={disabled}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            aria-label={`Digit ${i + 1} of ${LENGTH}`}
            aria-invalid={invalid || undefined}
            maxLength={i === 0 ? LENGTH : 1}
            onFocus={(e) => e.currentTarget.select()}
            onKeyDown={(e) => onKeyDown(i, e)}
            onPaste={(e) => onPaste(i, e)}
            onChange={(e) => {
              const v = e.target.value;
              // Autofill and some mobile keyboards put the whole code in one box.
              if (v.replace(/\D/g, "").length > 1) return fillFrom(i, v);
              const digit = v.replace(/\D/g, "").slice(-1);
              if (!digit) return;
              // Typing into a box past the first empty one fills the gap first, so there are never holes.
              const at = Math.min(i, value.length);
              commit(value.slice(0, at) + digit + value.slice(at + 1), at + 1 < LENGTH ? at + 1 : undefined);
            }}
            className={cn(
              "h-14 w-full min-w-0 max-w-12 rounded-lg border border-input-border bg-surface text-center font-mono text-2xl font-medium text-text shadow-xs tabular-nums",
              "caret-accent transition-[border-color,box-shadow] duration-150 hover:border-input-border-hover",
              "focus-visible:border-focus focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-focus/20",
              "disabled:cursor-not-allowed disabled:bg-surface-2 disabled:text-muted",
              d && "border-border-strong",
              invalid && "border-danger focus-visible:ring-danger/20",
            )}
          />
        </span>
      ))}
    </div>
  );
}
